import dayjs from 'dayjs';
import React, {ReactNode} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {DownloadIcon} from '../../assets/icons';
import BackArrow from '../../assets/icons/backArrow';
import DeleteIcon from '../../assets/icons/DeleteIcon';
import EditIcon from '../../assets/icons/EditIcon';
import PlusBoxIcon from '../../assets/icons/PlusBoxIcon';
import {GroupButton, SidebarDrawer} from '../../components/common';
import PageLoader from '../../components/common/PageLoader';
import {ExpenseDeletePrompt} from '../../components/expense/ExpenseDelete';
import {useDownloadExpense, useExpense} from '../../hooks';
import {formatMoney} from '../../utils';
import {ExpenseDetails} from "../../components/expense/expenseDetails";
import { convertExpense,getInvoice } from '../../backend-services';
import toast from 'react-hot-toast';
import LeftNotificationPopup from '../../components/core/NotificationPopup/LeftNotificationPopup';
import ProcessSuccessful from '../../assets/icons/ProcessSuccessful';
import { Table } from '../../components/common/Table/TableBody';

interface IGroupButton {
  text: string;
  icon: ReactNode;
  textColorClass?: string;
  onClick?: () => void;
  href?: string;
}

interface GroupButtonProps {
  buttons: IGroupButton[];
}

const ViewExpense = () => {
  const [showPrompt, setShowPrompt] = React.useState(false);
  const { isDownloading, downloadExpense } = useDownloadExpense();
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [showSuccessfulModal, setShowSuccessfulModal] = React.useState(false);
  const [invoices, setInvoices] = React.useState<any[]>([]);
const [isBillable, setIsBillable] =React.useState(false)


  const { expenseId } = useParams<{ expenseId: any }>();
  if (!expenseId) {
    throw new Error('Expense Id is missing');
  }
  const { expense, isLoading } = useExpense(expenseId);
  const [isConverting, setIsConverting] = React.useState(false);
  const [isConverted, setIsConverted] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const navigate = useNavigate();

  const handleDownloadClick = () => {
    //@ts-ignore
    downloadExpense([expense], expenseId);
  };

  const data = expense.lineItems.map((exp: any, i: any) => ({
    id: i + 1,
    name: exp?.name,
    quantity: exp?.quantity,
    rate: exp?.rate,
    amount: formatMoney(`${parseInt(exp.rate) * parseInt(exp.quantity)}`),
  }));

  const renderLineItemsData = () => {
    return data;
  };

  const renderLineItemsTableHead = () => {
    return {
      id: { title: '#' },
      name: { title: 'Items/Description' },
      quantity: { title: 'Quantity' },
      rate: { title: 'Rate' },
      amount: { title: 'Amount' },
    };
  };

  const handleExpenseDeleteSuccess = () => {
    setShowSuccessModal(true);
  };

  const handleConvertExpense = async (id: string) => {
    setIsConverting(true);
    try {
      const createdInvoice =  await convertExpense(expenseId);
      console.log(createdInvoice)
      setInvoices((prevInvoices) => [...prevInvoices, createdInvoice]);
      setIsConverting(false);
      setIsConverted(true);
      setShowSuccessfulModal(true);
      setTimeout(() => {
        navigate('/invoices');
      }, 2000); 
    } catch (error: any) {
      console.log(error);
      setErrorMessage('Unable to Convert Expense.');
      setIsConverting(false);
    }
  };
  

  const groupButtonProps: GroupButtonProps = {
    buttons: [
      {
        text: 'Edit',
        icon: <EditIcon />,
        href: `/expenses/edit/${expenseId}`,
      },
      {
        text: 'Download',
        icon: <DownloadIcon />,
        onClick: handleDownloadClick,
      },
    {
        text: 'Convert to Invoice',
        icon: <PlusBoxIcon />,
        onClick:handleConvertExpense,
      },
      {
        text: 'Delete',
        icon: <DeleteIcon />,
        onClick: () => {
          setShowPrompt(true);
        },
        textColorClass: 'text-[#FB0303]',
      },
    ].filter(Boolean) as IGroupButton[],
  };
 

  return (
    <div>
      {isLoading ? (
        <PageLoader></PageLoader>
      ) : (
        <>
          <div className="flex gap-[8.5px] ml-[-2.5em] 375:ml-[-2.5em] 414:ml-[-2em] md:ml-[2em]">
            <a href="/expenses">
              <BackArrow />
            </a>
            <h1 className="font-bold xl:text-[20px] text-[16px] md:">Expenses</h1>
          </div>
          <div className="flex flex-col gap-[50px] mt-[3em] lg:mt-[50px]">
            <p className="font-semibold text-[#6A32C6] xl:text-[25px] leading-5  tracking-wide whitespace-nowrap ml-[-2.2em] 375:ml-[-2.2em] 414:ml-[-1.7em] 360:ml-[-2.9em] 412:ml-[-1.7em] md:ml-[1.5em]">
              {dayjs(expense?.createdAt).format('MM/DD/YYYY')}
            </p>
            <div className='flex sm:flex-none justify-center lg:justify-center md:justify-start xl:mt-[-5.4em] mt-[-2em] md:ml-[2em] lg:mt-[-5em]'>
              <GroupButton {...groupButtonProps} />
            </div>
            {isDownloading && (
              <div className="flex items-center gap-[8px]">
                <span>Downloading...</span>
              </div>
            )}
            {showPrompt && (
              <>
            <SidebarDrawer 
            title={'Delete Expense'} 
            show={true} 
            position={'left'} 
            onClose={() => setShowPrompt(false)}>
              <ExpenseDeletePrompt 
              showPrompt={showPrompt} 
              show={() => setShowPrompt(false)}
              expenseId={expenseId} 
              onClose={() => setShowPrompt(false)}
              onDeleteSuccess={handleExpenseDeleteSuccess}
               />
             </SidebarDrawer>
             </>
            )}
         
          </div>

          <div>
            <p className="font-normal not-italic text-[ #000000] xl:text-[16px] leading-6 tracking-wide mt-[33px] ml-[-2.2em] 375:ml-[-2.2em] 414:ml-[-1.8em] 360:ml-[-2.9em] 412:ml-[-1.7em] md:ml-[2em]">
              Expense Amount
            </p>
            <span className="text-[#ef4444] xl:text-[25px] text-[16px] ml-[-2.2em]  375:ml-[-2.2em] 414:ml-[-1.8em] 360:ml-[-2.9em] 412:ml-[-1.7em] md:ml-[2em] xl:ml-[1.3em] 2xl:ml-[1.3em]">{`₦${expense?.amount?.toLocaleString()}`}</span>
          </div>
          <div className='375:w-[370px] w-[370px] 414:w-[400px] 412:w-[400px] md:w-[700px] justify-start flex mx-auto sm:mx-0 ml-[-2.2em] 375:ml-[-2.9em] 360:ml-[-3.5em] md:ml-[1.8em] lg:w-[650px] xl:w-[800px]'>

           <ExpenseDetails expense={expense} isBillable={isBillable} /> 
          </div>
          
          <div className="gap-[28px] mt-[3em]">
            <h3 className="text-[#00000] text-[16px] lg:text-[20px] font-semibold leading-6 ml-[-2.3em] 414:ml-[-1.7em] 412:ml-[-1.5em] md:ml-[1em]">Expense Breakdown</h3>
<div className='ml-[-2.2em] 414:ml-[-1.8em] 360:ml-[-3em] 412:ml-[-1.5em] md:ml-[1em] 375:ml-[-2.4em]'>
            <Table 
            expenseCard={true} 
            searchPlaceHolder="Search" 
            searchDisplayKey="id" 
            searchResult={[]} 
            head={renderLineItemsTableHead()} 
            data={renderLineItemsData()} 
            isLoading={isLoading} buttons={[]}  
            fetchMoreData={undefined} 
            meta={undefined}
          
            />
            </div>
          </div>

  {showSuccessfulModal && (
    <>
  <SidebarDrawer 
  title={'Invoice Created'} 
  show={true} 
  position={'left'} 
  onClose={() => setShowSuccessfulModal(false)}>
   <LeftNotificationPopup 
   title={''}
   icon={<ProcessSuccessful/>}
   onClose={() => setShowSuccessfulModal(false)}
   />
   </SidebarDrawer>
   </>
  )}
        </>
        
      )}
    </div>
  );
};

export { ViewExpense };